import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useNavBadges } from '@/hooks/useNavBadges'
import { useGameStore } from '@/store/gameStore'
import { COACH_HIDDEN_ROUTES } from '@/config/gameModesConfig'
import { NavBadge } from '../components/NavBadge'
import { QuickSaveButton } from '../components/QuickSaveButton'
import {
  LayoutDashboard,
  Home,
  Users,
  Wallet,
  Trophy,
  Ellipsis,
  Building2,
  Briefcase,
  Medal,
  Settings,
  type LucideIcon,
} from 'lucide-react'

interface NavItem { to: string; label: string; icon: LucideIcon }

// One ordered destination list; the first four become the primary tabs and the
// rest go under "Más". Coach mode hides the build/economy-of-the-club routes.
const ALL_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Inicio', icon: LayoutDashboard },
  { to: '/club', label: 'Club', icon: Home },
  { to: '/economy', label: 'Finanzas', icon: Wallet },
  { to: '/competitions', label: 'Liga', icon: Trophy },
  { to: '/career', label: 'Carrera', icon: Medal },
  { to: '/facilities', label: 'Instalaciones', icon: Building2 },
  { to: '/staff', label: 'Personal', icon: Users },
  { to: '/services', label: 'Servicios', icon: Briefcase },
  { to: '/settings', label: 'Ajustes', icon: Settings },
]

export function BottomNav() {
  const badges = useNavBadges()
  const gameMode = useGameStore(s => s.gameMode)
  const [moreOpen, setMoreOpen] = useState(false)

  const visible = gameMode === 'coach'
    ? ALL_ITEMS.filter(i => !COACH_HIDDEN_ROUTES.has(i.to))
    : ALL_ITEMS
  const PRIMARY = visible.slice(0, 4)
  const OVERFLOW = visible.slice(4)
  // Surface a hidden item's count (e.g. claimable rewards on Carrera) on the
  // "Más" button; fall back to a dot for plain boolean alerts.
  const overflowCount = OVERFLOW.reduce((n, i) => {
    const b = badges[i.to as keyof typeof badges]
    return n + (typeof b === 'number' ? b : 0)
  }, 0)
  const overflowDot = OVERFLOW.some(i => badges[i.to as keyof typeof badges] === true)
  const overflowBadge = overflowCount > 0 ? overflowCount : overflowDot

  const linkStyle = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
    padding: '0.3rem 0.5rem 0.25rem',
    textDecoration: 'none',
    color: isActive ? 'var(--gold)' : 'var(--text-secondary)',
    fontSize: '0.6875rem',
    fontFamily: 'var(--font-titles)',
    fontWeight: isActive ? 700 : 500,
    letterSpacing: '0.02em',
    transition: 'color 0.15s ease',
    position: 'relative',
  })

  return (
    <>
      {moreOpen && (
        <>
          <div
            onClick={() => setMoreOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 49 }}
          />
          <div
            className="glass-card-static"
            style={{
              position: 'fixed',
              bottom: `calc(var(--bottom-nav-height) + var(--safe-area-bottom) + 8px)`,
              right: 8,
              left: 8,
              zIndex: 51,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '0.4rem',
              padding: '0.6rem',
            }}
          >
            {OVERFLOW.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMoreOpen(false)}
                className="bottom-nav-link"
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.6rem 0.7rem', clipPath: 'var(--angle-clip)',
                  textDecoration: 'none',
                  background: isActive ? 'var(--gold-fill)' : 'var(--fill-soft)',
                  color: isActive ? 'var(--gold)' : 'var(--text-primary)',
                  fontSize: '0.8rem', fontWeight: 600, position: 'relative',
                })}
              >
                <span style={{ position: 'relative', display: 'inline-flex' }}>
                  <item.icon size={18} />
                  <NavBadge value={badges[item.to as keyof typeof badges]} size="sm" />
                </span>
                {item.label}
              </NavLink>
            ))}
            <QuickSaveButton variant="menu" onDone={() => setMoreOpen(false)} />
          </div>
        </>
      )}

      <nav
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: `calc(var(--bottom-nav-height) + var(--safe-area-bottom))`,
          paddingBottom: 'var(--safe-area-bottom)',
          background: 'var(--glass-bg)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderTop: '1px solid var(--glass-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          zIndex: 50,
        }}
      >
        {PRIMARY.map(item => (
          <NavLink key={item.to} to={item.to} className="bottom-nav-link" style={linkStyle}>
            {({ isActive }) => (
              <>
                {isActive && (
                  <span style={{
                    position: 'absolute', top: 0, width: 24, height: 3,
                    background: 'var(--gradient-gold)', clipPath: 'var(--angle-clip)',
                  }} />
                )}
                <span style={{ position: 'relative', display: 'inline-flex' }}>
                  <item.icon size={20} />
                  <NavBadge value={badges[item.to as keyof typeof badges]} size="sm" />
                </span>
                <span>{item.label}</span>
              </>
            )}
          </NavLink>
        ))}

        <button
          onClick={() => setMoreOpen(o => !o)}
          className="bottom-nav-link"
          style={{
            ...linkStyle({ isActive: moreOpen }),
            background: 'none', border: 'none', cursor: 'pointer',
          }}
        >
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <Ellipsis size={20} />
            <NavBadge value={overflowBadge} size="sm" />
          </span>
          <span>Más</span>
        </button>
      </nav>
    </>
  )
}
